import { useRef } from "react";
import { Tooltip, Link } from "@mui/material";
import { CloudUpload, DeleteSweep } from "@mui/icons-material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import OpenInNew from "@mui/icons-material/OpenInNew";
import UndoIcon from "@mui/icons-material/Undo";
import ReplyIcon from "@mui/icons-material/Reply";
import DocumentIcon from "@mui/icons-material/Description";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import {autoMap} from "../Behaviors/automap";
import {useProjects} from "../Behaviors/projects";
import {useAsperaApi} from "../aspera/aspera";
import { useStore } from "../State/zustandStore";
import { useIngestionJobs } from "../Behaviors/ingest.api";

export const AutoMapButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const { mapResourcesToTrack, project } = useProjects(projectId!);
    return (
        <Tooltip placement={"right"} title={"Auto Map"}>
            <AutoFixHighIcon className="icon-button icon--large"
                onClick={async () => {
                    try {
                        state.setProjectMessage(projectId!, "Mapping files");

                        const mappedResources = await autoMap(project!);

                        await mapResourcesToTrack(mappedResources);
                    } catch (error) {
                        console.error(`Automap error ${error}`);
                    } finally {
                        state.clearProjectMessage(projectId!);
                    }
                }
                } />
        </Tooltip>);
};
export const ResetMapButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const {resetMap} = useProjects(projectId!);
    return (
        <Tooltip placement={"right"} title={"Reset (Unmap all)"}>
            <UndoIcon className="icon-button icon--large"
                onClick={() => resetMap()} />
        </Tooltip>
    );
};

export interface Props {
    disabled?: boolean
}

export const UnMapAllButton = (props: Props) => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const {resetMap} = useProjects(projectId!);
    return (
        <Tooltip placement={"right"} title={"Unmap all files"}>
            <ClearAllIcon className={"icon-button "}
                onClick={async () => {
                    try {
                        state.setProjectMessage(projectId!, "Unmapping files");

                        await resetMap();

                    } catch (error) {
                        console.error(`Ummap error ${error}`);
                    } finally {
                        state.clearProjectMessage(projectId!);
                    }
                }
                } />
        </Tooltip>
    );
};

export const UploadAllButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const {project} = useProjects(projectId);
    // For upload all button, need to pass all resources otherwise only
    // mapped tracks will be uploaded for file based
    const resources = project?.resources ?? [];
    const {uploadResources} = useAsperaApi();
    return (<Tooltip title={"Upload all files now"}>
        <CloudUpload className="icon-button" onClick={async () => {
            state.logProjectEvent(projectId!, "Upload all clicked");
            state.setProjectMessage(projectId!, "Preparing Aspera Upload");
            await uploadResources(resources, projectId!);
            state.clearProjectMessage(projectId!);
        }
        } />
    </Tooltip>
    );
};

export const DeleteAllButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const {deleteResources, getUnmappedResources} = useProjects(projectId!);
    const {cancelUpload} = useAsperaApi();
    const deleteAll = async () => {
        const resources = getUnmappedResources();

        try {
            await cancelUpload(projectId!);
            await deleteResources(resources);
        } catch (error) {
            state.logProjectEvent(projectId!, `Error deleting all resources ${error}}`);
        }
    };
    return (
        <Tooltip title={"Delete all files"}>
            <DeleteSweep className="icon-button" onClick={() => deleteAll()} />
        </Tooltip>);
};

export const RecheckButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const {cancelUpload, uploadAll} = useAsperaApi();
    return <div className={"float-end"}>
        <Link href="#" underline="hover" color="secondary.dark" onClick={async () => {
            await cancelUpload(projectId!);
            await uploadAll(projectId!);
        }}>Restart upload</Link>&nbsp;&nbsp;
        <Tooltip title="Cancel current upload and initiate new upload">
            <RefreshIcon className="icon-button  "
                onClick={async () => {
                    await cancelUpload(projectId!);
                    await uploadAll(projectId!);
                }} />
        </Tooltip>
    </div>;
};

export const RefreshButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const {revalidateProject} = useProjects(projectId!);
    return <Tooltip title="Re-validate now">
        <RefreshIcon className="icon-button  "
            onClick={async () => { await revalidateProject("revalidate button", undefined); }}
        />
    </Tooltip>;
};

export const SendBackButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const { setIsIngestRequestedAfterUpload } = useProjects(projectId!);
    return <div className={"float-start"}>
        <Link href="#" underline="hover" color="secondary.dark"
            onClick={async () => { await setIsIngestRequestedAfterUpload(false, undefined, true); }}>
            Send back to Drafts</Link>&nbsp;&nbsp;
        <Tooltip title="Send this job back to Drafts">
            <ReplyIcon className="icon-button  "
                onClick={async () => { await setIsIngestRequestedAfterUpload(false, undefined, true); }}
            />
        </Tooltip>
    </div>;
};

export const StopButton = () => {
    const state = useStore().projects;
    const aspera = useAsperaApi();
    const projectId = state.selectedProjectId;
    const {setIsIngestRequestedAfterUpload} = useProjects(projectId!);
    return <div className={"float-start"}>
        <Link href="#" underline="hover" color="secondary.dark" onClick={async () => {
            await aspera.cancelUpload(projectId!);
            await setIsIngestRequestedAfterUpload(false);
        }}>Cancel Upload</Link>&nbsp;&nbsp;
        <Tooltip title="Cancel upload and move to Drafts">
            <CancelIcon className="icon-button  "
                onClick={async () => {
                    await aspera.cancelUpload(projectId!);
                    await setIsIngestRequestedAfterUpload(false);
                }} />
        </Tooltip>
    </div>;
};

export const DuplicateButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const {duplicateProject} = useProjects(projectId!);
    return <div className={"float-start"}>
        <Link href="#" underline="hover" color="secondary.dark" onClick={async () => {
            await duplicateProject(projectId!);
        }}>Create a new draft using these files</Link>&nbsp;&nbsp;
        <Tooltip title="Create a new draft using these files!">
            <ReplyIcon className="icon-button  "
                onClick={async () => {
                    await duplicateProject(projectId!);
                }
                } />
        </Tooltip>
    </div>;
};

export const ReportButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    //const navigate = useNavigate();

    const clickHandlerRpt = async () => {
        const urlToOpen = `/dr/${projectId}`;

        //navigate(`${urlToOpen}`);
        //Open the URL in a new tab/window
        window.open(urlToOpen, "_blank");
    };
    return (
        <div className="float-end">
            <Link href="#" underline="hover" onClick={clickHandlerRpt} color="secondary.dark">
                Discrepancy Report
            </Link>&nbsp;
            <Tooltip title="Display Discrepancy Report">
                <DocumentIcon className="icon-button"
                    onClick={clickHandlerRpt} />
            </Tooltip>
        </div>);
};

export const ViewInAstButton = () => {
    const state = useStore().projects;
    const projectId = state.selectedProjectId;
    const { project } = useProjects(projectId!);
    const jobId = project?.jobId;
    const { getJob } = useIngestionJobs();
    const job = getJob(jobId!);

    const clickHandlerAst = async () => {
        window.open(job.productUrl!, "_blank");
    };

    if ((job.productUrl?.length ?? 0) > 0) {
        return <div className={"float-end"}>
            <Link href="#" underline="hover" color="secondary.dark" onClick={clickHandlerAst}>
                View ingested assets in AST
            </Link>&nbsp;
            <Tooltip title="View ingested assets in AST">
                <OpenInNew className="icon-button" onClick={clickHandlerAst} />
            </Tooltip>
        </div>;
    }
    return <></>;
};

export const PrintWindowButton = () => {
    const myDivRef = useRef<HTMLDivElement>(null);
    return <div className={"float-end"} >
        <div ref={myDivRef} id="PrintButton">
            <Link href="#" underline="hover" color="secondary.dark"
                onClick={async () => {
                    const p = myDivRef.current;

                    p?.classList.add("no-print");
                    window.print();
                    p?.classList.remove("no-print");
                }
                }>
                Print
            </Link>&nbsp;
            <Tooltip title="Print this page!">
                <PrintIcon className="icon-button "
                    onClick={async () => {
                        const p = myDivRef.current;

                        p?.classList.add("no-print");
                        window.print();
                        p?.classList.remove("no-print");
                    }
                    } />
            </Tooltip>
        </div>
    </div>;
};
